import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/**
 * useLocationBeacon
 *
 * Starts a browser geolocation "watch" and posts the agent's live GPS
 * position to the backend every `intervalMs` (default 30 s) while `enabled`
 * is true. Also throttles beacons: even if the browser fires many high-
 * frequency position updates we only ship one per interval.
 *
 * Returns:
 *   {
 *     status:  'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable' | 'error',
 *     lastPos: {lat, lng, accuracy, timestamp} | null,
 *     lastSent: ISO string | null,
 *     error: string | null,
 *   }
 */
export function useLocationBeacon({ agentCode, enabled = true, intervalMs = 30000 } = {}) {
  const [status, setStatus] = useState('idle');
  const [lastPos, setLastPos] = useState(null);
  const [lastSent, setLastSent] = useState(null);
  const [error, setError] = useState(null);

  const watchIdRef = useRef(null);
  const lastSentAtRef = useRef(0);
  const inflightRef = useRef(false);

  useEffect(() => {
    if (!enabled || !agentCode) return;
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setStatus('unavailable');
      return;
    }

    setStatus('requesting');

    const sendBeacon = async (coords) => {
      const now = Date.now();
      if (now - lastSentAtRef.current < intervalMs) return;
      if (inflightRef.current) return;
      inflightRef.current = true;
      try {
        await axios.post(
          `${API_BASE}/agent/${agentCode}/location-beacon`,
          {
            lat: coords.latitude,
            lng: coords.longitude,
            accuracy_m: coords.accuracy,
            heading_deg: coords.heading ?? null,
            speed_mps: coords.speed ?? null,
          },
          { timeout: 8000 }
        );
        lastSentAtRef.current = Date.now();
        setLastSent(new Date(lastSentAtRef.current).toISOString());
      } catch (err) {
        // Silent — surface via error state, but do not stop watching
        setError(err?.response?.data?.detail || err?.message || 'beacon failed');
      } finally {
        inflightRef.current = false;
      }
    };

    const onSuccess = (pos) => {
      setStatus('granted');
      setError(null);
      const { latitude, longitude, accuracy, heading, speed } = pos.coords;
      setLastPos({
        lat: latitude,
        lng: longitude,
        accuracy,
        heading,
        speed,
        timestamp: new Date(pos.timestamp).toISOString(),
      });
      sendBeacon(pos.coords);
    };

    const onError = (err) => {
      if (err.code === err.PERMISSION_DENIED) {
        setStatus('denied');
      } else {
        setStatus('error');
      }
      setError(err.message || 'geolocation error');
    };

    watchIdRef.current = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 20000,
    });

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [agentCode, enabled, intervalMs]);

  return { status, lastPos, lastSent, error };
}

export default useLocationBeacon;
